// LatestMovement — compact "what changed for this signal this week" block,
// rendered inside the Market Signal Detail Panel. Reads the latest-week
// signal row matched by trackID. Tolerant of camelCase (normalised) and
// PascalCase (raw v4) field names.

import React from "react";
import { pipeFirstN, _accessors } from "../../lib/tracks/movement";
import { resolveMovementLists } from "../../lib/signals/evidence";

const {
    scoreDeltaOf,
    companyDeltaOf,
    companyTypeDeltaOf,
    articleDeltaOf,
    momentumOf,
    narrativeOf,
    evolutionOf,
    newCompaniesOf,
    droppedCompaniesOf,
    newCompanyTypesOf,
    newAssetClassesOf,
    newBlockchainsOf,
    companyTypeShiftOf,
} = _accessors;

const Stat = ({ label, value, hint, tone = "ink" }) => {
    const toneCls = tone === "positive" ? "text-[hsl(var(--positive))]" : tone === "negative" ? "text-[hsl(var(--negative))]" : "text-[hsl(var(--ink))]";
    return (
        <div className="flex flex-col">
            <div className="eyebrow">{label}</div>
            <div className={`text-base font-semibold num leading-tight ${toneCls}`}>{value ?? "—"}</div>
            {hint && <div className="text-[10px] text-[hsl(var(--mute))] mt-0.5">{hint}</div>}
        </div>
    );
};

const fmtDelta = (n) => {
    if (n > 0) return `+${n}`;
    if (n < 0) return `${n}`;
    return "0";
};

const tone = (n) => {
    if (n > 0) return "positive";
    if (n < 0) return "negative";
    return "ink";
};

const PipeList = ({ label, raw }) => {
    const items = pipeFirstN(raw, 5);
    if (items.length === 0) return null;
    const total = Array.isArray(raw)
        ? raw.length
        : (typeof raw === "string" ? raw.split("|").map((x) => x.trim()).filter(Boolean).length : 0);
    const more = total - items.length;
    return (
        <div className="text-xs text-[hsl(var(--ink-soft))] leading-relaxed mt-2">
            <span className="text-[hsl(var(--mute))]">{label}:</span>{" "}
            <span className="text-[hsl(var(--ink))]">{items.join(", ")}</span>
            {more > 0 && <span className="text-[hsl(var(--mute))]"> · +{more} more</span>}
        </div>
    );
};

const LatestMovement = ({ signal }) => {
    if (!signal) return null;

    const movement = resolveMovementLists(signal);

    const scoreDelta = scoreDeltaOf(signal);
    const companyDelta = companyDeltaOf(signal);
    const companyTypeDelta = companyTypeDeltaOf(signal);
    const articleDelta = articleDeltaOf(signal);
    const momentum = momentumOf(signal);
    const narrative = narrativeOf(signal) || evolutionOf(signal);
    const newCo = pipeFirstN(newCompaniesOf(signal), 5);
    const droppedCo = pipeFirstN(droppedCompaniesOf(signal), 5);
    const newTypes = pipeFirstN(newCompanyTypesOf(signal), 5);
    const newAssets = pipeFirstN(newAssetClassesOf(signal), 5);
    const newChains = pipeFirstN(newBlockchainsOf(signal), 5);
    const shift = companyTypeShiftOf(signal);

    const hasAnyDelta = scoreDelta !== 0 || companyDelta !== 0 || companyTypeDelta !== 0 || articleDelta !== 0;
    const hasAnyList = newCo.length || droppedCo.length || newTypes.length || newAssets.length || newChains.length;
    if (!hasAnyDelta && !hasAnyList && !narrative && !momentum) return null;

    return (
        <section className="border-t border-[hsl(var(--line))] pt-4 mt-4" data-testid="section-latest-movement">
            <div className="eyebrow">Latest movement</div>
            <div className="flex items-baseline gap-2 flex-wrap mt-0.5">
                <div className="text-sm font-semibold text-[hsl(var(--ink))]">Versus the previous week</div>
                {momentum && (
                    <span className="text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[hsl(var(--surface-sunken))] text-[hsl(var(--ink-soft))]">{momentum}</span>
                )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                <Stat label="Score Δ" value={fmtDelta(scoreDelta)} tone={tone(scoreDelta)} />
                <Stat label="Companies Δ" value={fmtDelta(companyDelta)} tone={tone(companyDelta)} />
                <Stat label="Actor types Δ" value={fmtDelta(companyTypeDelta)} tone={tone(companyTypeDelta)} />
                <Stat label="Articles Δ" value={fmtDelta(articleDelta)} tone={tone(articleDelta)} />
            </div>
            <PipeList label="New actor groups" raw={newCompanyTypesOf(signal)} />
            <PipeList label="New companies" raw={movement.newCompanies.length ? movement.newCompanies : newCompaniesOf(signal)} />
            {movement.showDropped ? (
                <PipeList label="Dropped companies" raw={movement.droppedCompanies.length ? movement.droppedCompanies : droppedCompaniesOf(signal)} />
            ) : (
                <div className="mt-2 px-2.5 py-2 rounded border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/8 text-[11px] leading-relaxed text-[hsl(var(--warning))]" data-testid="dropped-companies-caution">
                    <div className="font-medium">Dropped companies suppressed</div>
                    <p className="text-[hsl(var(--ink-soft))] mt-1">{movement.guard.note || "Prior comparison basis was low-coherence/noise; dropped entities should be interpreted cautiously."}</p>
                </div>
            )}
            <PipeList label="New asset classes" raw={newAssetClassesOf(signal)} />
            <PipeList label="New blockchains" raw={newBlockchainsOf(signal)} />
            {shift && (
                <div className="text-xs text-[hsl(var(--ink-soft))] leading-relaxed mt-2">
                    <span className="text-[hsl(var(--mute))]">Actor-type shift:</span>{" "}
                    <span className="text-[hsl(var(--ink))]">{shift}</span>
                </div>
            )}
            {narrative && (
                <p className="mt-3 text-xs italic text-[hsl(var(--ink-soft))] leading-relaxed border-l-2 border-[hsl(var(--accent))] pl-3" data-testid="movement-narrative">{narrative}</p>
            )}
        </section>
    );
};

export default LatestMovement;
